import cloneDeep from 'lodash.clonedeep';
import { action, computed, observable, makeObservable } from 'mobx';
import { defaultOrderSystemNotification, defaultOrderSystemNotificationValidation } from '../constants';
import { getOrderSystemNotificationParams, orderSystemNotificationCast } from '../helpers';
import { IOrderSystemNotification, IOrderSystemNotificationValidation, IResponseOrderSystemNotification } from '../types';
import client from 'src/services/client';
import { LoadingStatus } from 'src/types/main';
import { emptyNameValidation } from 'src/utils/validation';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';

class OrderSystemNotificationStore {
  constructor() {
    makeObservable(this);
  }

  @observable initial: boolean = true;
  private fetchAbortController = getAbortController();
  private updateAbortController = getAbortController();

  private currentSavedNotification: IOrderSystemNotification = defaultOrderSystemNotification;
  @observable notification: IOrderSystemNotification = defaultOrderSystemNotification;

  @observable loading: LoadingStatus = LoadingStatus.Init;
  @observable isEdit: boolean = false;

  @computed get isLoading(): boolean {
    return this.loading === LoadingStatus.Loading;
  }

  @action initStore = () => {
    this.validation = cloneDeep(defaultOrderSystemNotificationValidation);
    this.isEdit = false;

    this.fetchNotification();
  };

  private setNotification = (data: IResponseOrderSystemNotification) => {
    const notification: IOrderSystemNotification = orderSystemNotificationCast(data);

    this.notification = notification;
    this.currentSavedNotification = notification;

    this.initial = false;
  };

  private fetchNotification = async () => {
    this.fetchAbortController.abort();
    this.fetchAbortController = getAbortController();

    try {
      this.loading = LoadingStatus.Loading;

      const response: IResponseOrderSystemNotification = await client.get('v3/settings/notifications/system', {
        signal: this.fetchAbortController.signal,
      });

      this.setNotification(response);
      this.loading = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loading = LoadingStatus.Success;
        return;
      }

      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'OrderSystemNotificationStore fetchOrderSystemNotification', showModal: true });
    }
  };

  @action changeNotification = (value: Partial<IOrderSystemNotification>) => {
    this.notification = { ...this.notification, ...value };
    this.isEdit = true;

    // update validation
    if (!this.validation.isValid) {
      this.setValidation();
    }
  };

  @action resetNotification = () => {
    this.validation = cloneDeep(defaultOrderSystemNotificationValidation);
    this.notification = this.currentSavedNotification;
    this.isEdit = false;
  };

  @action updateNotification = async () => {
    this.updateAbortController.abort();
    this.updateAbortController = getAbortController();

    try {
      this.loading = LoadingStatus.Loading;

      const params = getOrderSystemNotificationParams(this.notification);

      const response: IResponseOrderSystemNotification = await client.put('v3/settings/notifications/system', params, {
        signal: this.updateAbortController.signal,
      });

      this.setNotification(response);
      this.isEdit = false;
      this.loading = LoadingStatus.Success;
      return true;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loading = LoadingStatus.Success;
        return;
      }

      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'OrderSystemNotificationStore updateOrderSystemNotification', showModal: true });
    }
  };

  @observable validation: IOrderSystemNotificationValidation = cloneDeep(defaultOrderSystemNotificationValidation);
  @action setValidation = () => {
    this.validation = cloneDeep(defaultOrderSystemNotificationValidation);

    [this.validation.otp, this.validation.isValid] = emptyNameValidation(this.notification.otp, this.validation.otp, this.validation.isValid);

    // [this.validation.address, this.validation.isValid] = emptyNameValidation(
    //   this.notification.address,
    //   this.validation.address,
    //   this.validation.isValid
    // );

    [this.validation.refund, this.validation.isValid] = emptyNameValidation(
      this.notification.refund,
      this.validation.refund,
      this.validation.isValid
    );

    return this.validation.isValid;
  };
}

export const orderSystemNotificationStore = new OrderSystemNotificationStore();
